import { alphabetical, isEmpty, sift, unique } from 'radash'
import { format, parse } from 'date-fns'
import type {
  AdhocBookingBookedTransportation,
  GetBookingTransportationResponse,
} from '@/types/booking'
import {
  BOOKING_TRIP_DIRECTION,
  BOOKING_TRIP_TYPE,
  BUS_TRIP_TYPES,
  POINT_TO_POINT_CATEGORY,
} from '@/utils/constant'
import { toTimeSlot } from '@/utils/date'

export const transformTransportation = (
  transportation: AdhocBookingBookedTransportation,
): AdhocBookingBookedTransportation => {
  const _adhoc_booking_booked_products = unique(
    [
      ...(transportation?.booked_transportation_hourly
        ?.adhoc_booking_booked_products ?? []),
      ...(transportation?.booked_transportation_point_to_points?.flatMap(
        (point) => point?.adhoc_booking_booked_products ?? [],
      ) ?? []),
    ].filter((item) => !isEmpty(item.product)),
    (item) => item.product?.id,
  )
  const departPoint = transportation.booked_transportation_point_to_points
    ?.length
    ? transportation.booked_transportation_point_to_points.find(
        (point) => point.category === POINT_TO_POINT_CATEGORY.DEPART_TRIP,
      )
    : null
  const timeSlot = transportation.booked_transportation_hourly?.pickup_time
    ? toTimeSlot(transportation.booked_transportation_hourly.pickup_time)
    : departPoint?.pickup_time
      ? toTimeSlot(departPoint.pickup_time)
      : undefined
  const category =
    transportation.trip_type === BOOKING_TRIP_TYPE.HOURLY_RENTAL
      ? BUS_TRIP_TYPES.HOURLY
      : transportation.trip_type === BOOKING_TRIP_TYPE.POINT_TO_POINT &&
          transportation.point_to_point_type === BOOKING_TRIP_DIRECTION.ONE_WAY
        ? BUS_TRIP_TYPES.DEPART_TRIP
        : BUS_TRIP_TYPES.RETURN_TRIP

  const configTime =
    transportation.booked_transportation_hourly?.config_time ??
    departPoint?.config_time ??
    undefined

  const monthYear = !transportation.departure_date
    ? undefined
    : format(
        parse(transportation.departure_date, 'yyyy-MM-dd', new Date()),
        'yyyy-MM',
      )
  const isFilled =
    !isEmpty(transportation.trip_type) &&
    !isEmpty(transportation.config_days) &&
    !isEmpty(
      transportation.booked_transportation_hourly?.pickup_time ||
        sift(
          transportation.booked_transportation_point_to_points?.map(
            (point) => point.pickup_time,
          ) ?? [],
        ),
    ) &&
    !isEmpty(
      transportation.booked_transportation_hourly?.pickup_address ||
        sift(
          transportation.booked_transportation_point_to_points?.map(
            (point) => point.pickup_address,
          ) ?? [],
        ),
    )
  return {
    ...transportation,
    ...(transportation.booked_transportation_hourly
      ? {
          booked_transportation_hourly: {
            ...transportation.booked_transportation_hourly,
            adhoc_booking_booked_products:
              transportation.booked_transportation_hourly.adhoc_booking_booked_products.filter(
                (product) => product.product,
              ),
          },
        }
      : {}),
    ...(transportation.booked_transportation_point_to_points?.length
      ? {
          booked_transportation_point_to_points: alphabetical(
            transportation.booked_transportation_point_to_points,
            (p) => p.category,
          ).map((point) => ({
            ...point,
            adhoc_booking_booked_products:
              point.adhoc_booking_booked_products.filter(
                (product) => product.product,
              ),
          })),
        }
      : {}),
    _is_filled: isFilled,
    _adhoc_booking_booked_products,
    _time_slot: timeSlot,
    _category: category,
    _config_time: configTime,
    _month_year: monthYear,
    _default_tab:
      category === BUS_TRIP_TYPES.RETURN_TRIP
        ? BUS_TRIP_TYPES.DEPART_TRIP
        : undefined,
  }
}

export const transformDetailTransportation = (
  res: GetBookingTransportationResponse,
) => ({
  ...res,
  data: {
    ...res.data,
    booked_transportation: {
      ...res.data.booked_transportation,
      ...transformTransportation(res.data.booked_transportation),
    },
  },
})
